import { createContext } from "react";
import type { Funder, RoundAggregate } from "@engine/round/aggregate";

/**
 * Client-side round settings, in a LEAF module. Extracted from Round.tsx to
 * break a module cycle: Round renders the EvaluablePage components while
 * they imported this context back from Round — bun's Linux evaluation order
 * could enter that cycle from a component and hit the context's TDZ
 * ("Cannot access 'ClientSettingsContext' before initialization" in CI).
 * This module imports no component code, so it can't re-enter a cycle.
 */

export function getStartingSimulationHorizon(round: RoundAggregate) {
  const funders = Object.values(round.members).flatMap((member) =>
    Object.values(member.evaluators).filter(
      (evaluator) =>
        evaluator.type === "funder" && evaluator.status === "active",
    ),
  ) as Funder[];

  let totalBudget = 0;

  for (const funder of funders) {
    totalBudget += funder.budget;
  }

  return totalBudget;
}

export const getSimulationHorizonFromLocalStorage = (
  round: RoundAggregate,
): number => {
  const storedValue = localStorage.getItem("simulationHorizon");
  return storedValue !== null
    ? Number(storedValue)
    : getStartingSimulationHorizon(round);
};

export const setSimulationHorizonToLocalStorage = (value: number) =>
  localStorage.setItem("simulationHorizon", String(value));

export const ClientSettingsContext = createContext<{
  getSimulationHorizon: (round: RoundAggregate) => number;
  setSimulationHorizon: (n: number) => void;
  aiFeaturesEnabled: boolean;
  setAiFeaturesEnabled: (enabled: boolean) => void;
}>({
  getSimulationHorizon: (round) => getSimulationHorizonFromLocalStorage(round),
  setSimulationHorizon: (number) => setSimulationHorizonToLocalStorage(number),
  aiFeaturesEnabled: true,
  setAiFeaturesEnabled: () => {},
});
